import { WORKSPACE_STORAGE_TYPE } from '@bangle.io/constants';
import type { WorkspaceOpsService } from '@bangle.io/service-core';
import { listMyWorkspaces } from '@bangle.io/supabase';
import type { WorkspaceInfo } from '@bangle.io/types';
import { WsPath } from '@bangle.io/ws-path';

const PENDING_KEY = 'sariz-notes:pending-cloud-workspaces';
const PENDING_OPEN_KEY = 'sariz-notes:pending-open-cloud-workspace';

// Characters forbidden in wsName (`validateWsName`), plus path separators.
// biome-ignore lint/suspicious/noControlCharactersInRegex: Strip control chars from titles.
const INVALID_WS_NAME_CHARS = /[<>:"\\|?*/\\\x00-\x1F]+/g;

export type PendingCloudWorkspace = {
  workspaceId: string;
  title: string;
};

export type SyncCloudWorkspacesResult = {
  nameById: Map<string, string>;
  /** Old local wsName → new local wsName when a cloud title was restored. */
  renames: Map<string, string>;
};

export function queuePendingCloudWorkspace(entry: PendingCloudWorkspace) {
  const existing = readPendingCloudWorkspaces();
  const next = [
    ...existing.filter((e) => e.workspaceId !== entry.workspaceId),
    entry,
  ];
  localStorage.setItem(PENDING_KEY, JSON.stringify(next));
}

export function readPendingCloudWorkspaces(): PendingCloudWorkspace[] {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as PendingCloudWorkspace[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function clearPendingCloudWorkspaces() {
  try {
    localStorage.removeItem(PENDING_KEY);
  } catch {
    // Ignore missing localStorage (tests / non-browser).
  }
}

export function queuePendingOpenWorkspace(workspaceId: string) {
  try {
    localStorage.setItem(PENDING_OPEN_KEY, workspaceId);
  } catch {
    // Ignore missing localStorage (tests / non-browser).
  }
}

export function consumePendingOpenWorkspace(): string | null {
  try {
    const value = localStorage.getItem(PENDING_OPEN_KEY);
    localStorage.removeItem(PENDING_OPEN_KEY);
    return value;
  } catch {
    return null;
  }
}

/**
 * Map a cloud workspace title to a local wsName.
 * Preserves spaces and letters; only strips characters that wsName forbids.
 */
export function cloudTitleToWsName(title: string): string {
  const collapsed = title
    .replace(INVALID_WS_NAME_CHARS, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (!collapsed || collapsed === '.') {
    return 'notes';
  }

  const result = WsPath.safeFromParts(collapsed, '');
  return result.ok ? collapsed : 'notes';
}

export function uniqueWsName(desired: string, taken: Set<string>): string {
  let candidate = desired;
  let i = 2;
  while (taken.has(candidate)) {
    candidate = `${desired}-${i}`;
    i += 1;
  }
  return candidate;
}

function supabaseWorkspaceId(ws: WorkspaceInfo): string | null {
  if (ws.type !== WORKSPACE_STORAGE_TYPE.Supabase) return null;
  const id = ws.metadata?.workspaceId;
  return typeof id === 'string' ? id : null;
}

/**
 * Create or rename local WorkspaceInfo rows so they match cloud titles
 * (spaces preserved). Used by {@link syncCloudWorkspacesLocally}.
 */
export async function materializeCloudWorkspacesLocally(
  workspaceOps: WorkspaceOpsService,
  byId: Map<string, string>,
): Promise<SyncCloudWorkspacesResult> {
  const existing = await workspaceOps.getAllWorkspaces({});
  const renames = new Map<string, string>();

  for (const [workspaceId, title] of byId) {
    const desiredName = cloudTitleToWsName(title);
    const existingWs = existing.find(
      (w) => !w.deleted && supabaseWorkspaceId(w) === workspaceId,
    );

    if (existingWs) {
      if (existingWs.name === desiredName) {
        continue;
      }

      const taken = new Set(
        existing.filter((w) => !w.deleted).map((w) => w.name),
      );
      // Only reclaim the spaced title when it is free; otherwise leave the
      // existing local name alone (collision with an unrelated workspace).
      if (taken.has(desiredName)) {
        continue;
      }

      await workspaceOps.createWorkspaceInfo({
        name: desiredName,
        type: WORKSPACE_STORAGE_TYPE.Supabase,
        metadata: { ...existingWs.metadata, workspaceId },
      });
      await workspaceOps.deleteWorkspaceInfo(existingWs.name);
      renames.set(existingWs.name, desiredName);

      existingWs.deleted = true;
      existing.push({
        name: desiredName,
        type: WORKSPACE_STORAGE_TYPE.Supabase,
        lastModified: Date.now(),
        metadata: { ...existingWs.metadata, workspaceId },
      });
      continue;
    }

    const taken = new Set(
      existing.filter((w) => !w.deleted).map((w) => w.name),
    );
    const candidate = uniqueWsName(desiredName, taken);
    await workspaceOps.createWorkspaceInfo({
      name: candidate,
      type: WORKSPACE_STORAGE_TYPE.Supabase,
      metadata: { workspaceId },
    });
    existing.push({
      name: candidate,
      type: WORKSPACE_STORAGE_TYPE.Supabase,
      lastModified: Date.now(),
      metadata: { workspaceId },
    });
  }

  const nameById = new Map<string, string>();
  for (const ws of existing) {
    if (ws.deleted) continue;
    const workspaceId = supabaseWorkspaceId(ws);
    if (workspaceId) {
      nameById.set(workspaceId, ws.name);
    }
  }
  return { nameById, renames };
}

/**
 * Soft-delete every local WorkspaceInfo row backed by cloud (Supabase) storage.
 * Used when the user signs out so cloud notebooks no longer appear in the
 * local workspace list.
 */
export async function removeLocalCloudWorkspaces(
  workspaceOps: WorkspaceOpsService,
): Promise<string[]> {
  const existing = await workspaceOps.getAllWorkspaces({
    type: WORKSPACE_STORAGE_TYPE.Supabase,
  });
  const removed: string[] = [];
  for (const ws of existing) {
    await workspaceOps.deleteWorkspaceInfo(ws.name);
    removed.push(ws.name);
  }
  clearPendingCloudWorkspaces();
  try {
    localStorage.removeItem(PENDING_OPEN_KEY);
  } catch {
    // Ignore missing localStorage (tests / non-browser).
  }
  return removed;
}

/**
 * Soft-delete local cloud workspaces that are no longer in the remote
 * membership set (and not pending a join).
 */
async function pruneStaleLocalCloudWorkspaces(
  workspaceOps: WorkspaceOpsService,
  keepIds: Set<string>,
): Promise<string[]> {
  const existing = await workspaceOps.getAllWorkspaces({
    type: WORKSPACE_STORAGE_TYPE.Supabase,
  });
  const removed: string[] = [];
  for (const ws of existing) {
    const workspaceId = supabaseWorkspaceId(ws);
    if (!workspaceId || keepIds.has(workspaceId)) {
      continue;
    }
    await workspaceOps.deleteWorkspaceInfo(ws.name);
    removed.push(ws.name);
  }
  return removed;
}

export type SyncCloudWorkspacesOptions = {
  /**
   * When true, an empty remote membership list is trusted and local cloud
   * rows not in that set are pruned. Callers must only set this after a
   * successful {@link ensureFreshSession} — otherwise a dead JWT that yields
   * empty ACL results would wipe every local cloud notebook.
   */
  authVerified?: boolean;
};

/**
 * Ensure local WorkspaceInfo rows exist for every cloud workspace the user
 * belongs to (and any pending join results). Removes local cloud rows that
 * are no longer in that set when {@link SyncCloudWorkspacesOptions.authVerified}
 * is set.
 */
export async function syncCloudWorkspacesLocally(
  workspaceOps: WorkspaceOpsService,
  options: SyncCloudWorkspacesOptions = {},
): Promise<SyncCloudWorkspacesResult> {
  const remote = await listMyWorkspaces();
  const pending = readPendingCloudWorkspaces();
  const byId = new Map<string, string>();
  for (const ws of remote) {
    byId.set(ws.id, ws.title);
  }
  for (const p of pending) {
    if (!byId.has(p.workspaceId)) {
      byId.set(p.workspaceId, p.title);
    }
  }

  const keepIds = new Set(byId.keys());
  if (keepIds.size > 0 || options.authVerified) {
    await pruneStaleLocalCloudWorkspaces(workspaceOps, keepIds);
  } else {
    console.warn(
      'cloud workspace sync returned no memberships without a verified session; skipping prune',
    );
  }
  const result = await materializeCloudWorkspacesLocally(workspaceOps, byId);
  clearPendingCloudWorkspaces();
  return result;
}
